import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

import colors from "core/colors";
import breakpoints from "core/breakpoints";
import { spacingWithBreakpoints } from "core/spacing";
import {
    textMd,
    headingXs,
    headingMd,
    headingLg,
    medium,
    bold
} from "core/fontStyles";
import { PHOTO_LICENSE_PATH } from "data/links";
import { STANDARD_PHOTO_PRICE, PREMIUM_PHOTO_PRICE } from "core/constants";

import Link from "core/Link";
import SectionContainer from "core/SectionContainer";
import CheckmarkIcon from "core/ReusableIcons/Checkmark";
import CloseIcon from "core/ReusableIcons/Close";
import PhotoIcon from "core/ReusableIcons/Photo";
import CCIcon from "core/ReusableIcons/CC";
import CrownIcon from "core/ReusableIcons/Crown";

import {
    MarketingPageSectionHeadingContainer,
    MarketingPageSectionEyebrowSubheading,
    MarketingPageSectionHeading,
    MarketingPageSectionBiLine
} from "core/MarketingPageSectionHeading";
import { Footnote, PageAnchor } from "./styles";

import { heroCardData } from "./data";

export const PhotosSectionHeadingContainer = styled(
    MarketingPageSectionHeadingContainer
)`
    ${spacingWithBreakpoints.XL3Bottom}
`;

const PricingGrid = styled.section`
    margin: 0 auto;
    width: 100%;
    ${spacingWithBreakpoints.LGBottom}
    ${textMd}
    ${medium}

    display: grid;
    grid-template-columns: 1fr;

    @media (min-width: ${breakpoints.tablet.rem}) {
        max-width: 58.75rem;
        grid-template-columns: 1fr 1fr;
    }
`;

const PricingHeader = styled.header`
    display: block;
    text-align: center;
    padding: 1.5rem 0;
    border-bottom: 1px ${colors.primary.scheifer} solid;

    h2 {
        ${headingMd}
        margin-bottom: 1rem;
    }
`;

const PricingTypeText = styled.h2`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    ${headingMd}
    margin-bottom: 1rem;
`;

const PricingScopeText = styled.p`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    ${headingXs}
`;

const PriceText = styled.p`
    ${headingLg}
`;

const PricingCol = styled.div`
    display: grid;
    grid-template-rows: repeat(4, auto);
    border: 1px ${colors.primary.scheifer} solid;

    &:last-child {
        margin-top: 2rem;
    }

    .strikethrough {
        text-decoration: line-through;
    }

    > div {
        padding: 0.25rem 1.5rem;

        display: flex;
        align-items: center;

        &.no-icon {
            svg {
                visibility: hidden;
            }
        }

        svg {
            margin-right: 0.5rem;
            width: 0.75rem;
        }

        &:nth-child(2) {
            padding-top: 1.5rem;
        }

        &:last-child {
            padding-bottom: 1.5rem;
        }
    }

    @media (min-width: ${breakpoints.tablet.rem}) {
        &:last-child {
            margin-top: 0;
            border-left: none;
        }

        > div {
            padding: 0.5rem 2rem;
            border-bottom: 1px ${colors.primary.scheifer} solid;

            svg {
                margin-right: 0.5rem;
                width: 1rem;
            }

            &:nth-child(2) {
                padding-top: 0.75rem;
            }

            &:last-child {
                padding-bottom: 0.75rem;
                border-bottom: none;
            }
        }
    }

    @media (min-width: ${breakpoints.laptop.rem}) {
        > div {
            padding: 0.75rem 3rem;
        }
    }
`;

const RoyaltyFreeOptions = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;

    > div {
        margin-right: 1rem;
        &:last-child {
            margin-right: 0;
        }
    }
`;

const DisclaimerSection = styled.div`
    display: flex;
    gap: 1.5rem;
    width: 100%;
    margin-bottom: 1.5rem;
    flex-direction: column;

    @media (min-width: ${breakpoints.tablet.rem}) {
        max-width: 58.75rem;
        margin: auto;
        margin-bottom: 1.5rem;
        flex-direction: row;
    }
`;

const DisclaimerCard = styled.div`
    flex: 1;
    border: 1px ${colors.primary.scheifer} solid;
    padding: 1.5rem;
`;

const DisclaimerHeading = styled.h3`
    display: flex;
    gap: 0.5rem;
    align-items: center;
    margin-bottom: 1rem;
    ${textMd}
    ${bold}
`;

const DisclaimerList = styled.ul`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    list-style: disc;
    list-style-position: inside;
    padding-left: 1rem;
`;

const trueColor = colors.accent.lightMode.persian;
const falseColor = colors.accent.darkMode.suenosBuenos;
const featureIconYes = <CheckmarkIcon fillColor={trueColor} />;
const featureIconNo = <CloseIcon fillColor={falseColor} />;

const PhotosPricingPremium = ({ hideNounProjectHeading }) => {
    return (
        <PageAnchor id={heroCardData[1].sectionId}>
            <SectionContainer bgColor={colors.primary.diamond}>
                <PhotosSectionHeadingContainer>
                    {!hideNounProjectHeading && (
                        <MarketingPageSectionEyebrowSubheading>
                            Noun Project
                        </MarketingPageSectionEyebrowSubheading>
                    )}
                    <MarketingPageSectionHeading
                        type="h2"
                        size="xl"
                        alignment="center"
                    >
                        Photos Pricing
                    </MarketingPageSectionHeading>
                    <MarketingPageSectionBiLine>
                        Our mission-driven, art-quality photo collection grows
                        daily. Photos are available in both free and paid
                        options.
                    </MarketingPageSectionBiLine>
                </PhotosSectionHeadingContainer>
                <PricingGrid>
                    <PricingCol>
                        <PricingHeader>
                            <PricingTypeText>
                                Photo Comp Downloads <CCIcon />
                            </PricingTypeText>
                            <PricingScopeText>
                                Standard Photos Only
                            </PricingScopeText>
                            <PriceText>$0</PriceText>
                            <p>Attribution required</p>
                        </PricingHeader>
                        <div className="no-icon">
                            <CheckmarkIcon fillColor={trueColor} />
                            <Link href={PHOTO_LICENSE_PATH} target="_blank">
                                Creative Commons (CC BY-NC-ND 2.0)
                            </Link>
                        </div>
                        <div className="no-icon">
                            <CheckmarkIcon fillColor={trueColor} />
                            .2 MP Resolution
                        </div>
                        <div className="no-icon">
                            <CheckmarkIcon fillColor={trueColor} />
                            One print project
                        </div>
                        <div>
                            <CheckmarkIcon fillColor={trueColor} />
                            Use worldwide, in-perpetuity
                        </div>
                        <div>
                            {featureIconNo}
                            <span className="strikethrough">Crop image</span>
                        </div>
                        <div>
                            {featureIconNo}
                            <span className="strikethrough">
                                Use without attribution
                            </span>
                        </div>
                        <div>
                            {featureIconNo}
                            <span className="strikethrough">
                                Edit, build upon, make derivative
                            </span>
                        </div>
                        <div>
                            {featureIconNo}
                            <span className="strikethrough">
                                Commercial use
                            </span>
                        </div>
                    </PricingCol>

                    <PricingCol>
                        <PricingHeader>
                            <PricingTypeText>
                                Royalty-Free Downloads
                            </PricingTypeText>
                            <RoyaltyFreeOptions>
                                <div>
                                    <PricingScopeText>
                                        Standard Photos
                                    </PricingScopeText>
                                    <PriceText>
                                        {`$${STANDARD_PHOTO_PRICE}`}
                                    </PriceText>
                                    <p>Great for most uses</p>
                                </div>
                                <div>or</div>
                                <div>
                                    <PricingScopeText>
                                        Premium Photos{" "}
                                        <CrownIcon
                                            fillColor={
                                                colors.secondary.sunshine
                                            }
                                        />
                                    </PricingScopeText>
                                    <PriceText>
                                        {`$${PREMIUM_PHOTO_PRICE}`}
                                    </PriceText>
                                    <p>Top art quality</p>
                                </div>
                            </RoyaltyFreeOptions>
                        </PricingHeader>
                        <div className="no-icon">
                            {featureIconYes}
                            <Link href={PHOTO_LICENSE_PATH} target="_blank">
                                Royalty-Free Commercial License
                            </Link>
                        </div>
                        <div className="no-icon">
                            {featureIconYes}
                            High Resolution
                        </div>
                        <div className="no-icon">
                            {featureIconYes}
                            Up to 250,000 project prints
                        </div>
                        <div>
                            {featureIconYes}
                            Use worldwide, in-perpetuity
                        </div>
                        <div>
                            {featureIconYes}
                            Crop image
                        </div>
                        <div>
                            {featureIconYes}
                            Use without attribution
                        </div>
                        <div>
                            {featureIconYes}
                            Edit, build upon, make derivative
                        </div>
                        <div>
                            {featureIconYes}
                            Commercial use
                        </div>
                    </PricingCol>
                </PricingGrid>

                <DisclaimerSection>
                    <DisclaimerCard>
                        <DisclaimerHeading>
                            <CloseIcon
                                size="xl"
                                fillColor={colors.functional.lightMode.sangria}
                            />{" "}
                            * Please note for all photos:
                        </DisclaimerHeading>
                        <DisclaimerList>
                            <li>May not resell photo</li>
                            <li>May not use in digital templates</li>
                            <li>No multi-seat team use</li>
                        </DisclaimerList>
                    </DisclaimerCard>
                    <DisclaimerCard>
                        <DisclaimerHeading>
                            <PhotoIcon size="xl" fillColor={trueColor} /> *
                            Photos marked “Editorial Use Only”:
                        </DisclaimerHeading>
                        <DisclaimerList>
                            <li>May only be used non-commercially</li>
                            <li>Not cleared for promotional use</li>
                            <li>Not cleared for use in merchandising</li>
                        </DisclaimerList>
                    </DisclaimerCard>
                </DisclaimerSection>

                <Footnote>
                    *Please see{" "}
                    <Link
                        href={PHOTO_LICENSE_PATH}
                        target="_blank"
                        size="md"
                        style="primary"
                    >
                        license terms
                    </Link>{" "}
                    for complete details.
                </Footnote>
            </SectionContainer>
        </PageAnchor>
    );
};

PhotosPricingPremium.propTypes = {
    hideNounProjectHeading: PropTypes.bool
};

export default PhotosPricingPremium;
